// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { cartLinkStyles } from "@insite/content-library/Widgets/Common/CartLink";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Modal, { ModalProps } from "@store/MobiusOverrides/Modal/Modal";
import clearUploadedFileStatus from "@store/Framework/Store/Greif/Components/GreifOrderUpload/Handlers/ClearFileStatus";
import createCartUpload from "@store/Framework/Store/Greif/Components/GreifOrderUpload/Handlers/CreateCartUpload";
import submitUploadedOrder from "@store/Framework/Store/Greif/Components/GreifOrderUpload/Handlers/SubmitUploadedOrder";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    confirmationText = "confirmationText",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.confirmationText]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    orderUploadState: state.greif.components.greifOrderUpload,
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
});

const mapDispatchToProps = {
    createCartUpload,
    submitUploadedOrder,
    clearUploadedFileStatus,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderUploadSubmitButtonStyles {
    wrapper?: InjectableCss;
    confirmButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    modal?: ModalProps;
    modalActions?: InjectableCss;
}

export const orderUploadSubmitButtonStyles: OrderUploadSubmitButtonStyles = {
    wrapper: {
        css: css`
            margin-top: ${pxToRem(80)};
            border-top: ${pxToRem(1)} solid ${getColor("common.border")};
            padding-top: ${pxToRem(32)};
        `,
    },
    confirmButton: {
        variant: "primary",
        sizeVariant: "large",
        css: css`
            ${x => x.theme.button.primary.css}
            ${breakpointValues(["100%", null, "auto"], "width")}
        `,
    },
    submitButton: {
        variant: "primary",
        css: css`
            ${x => x.theme.button.primary.css}
            ${breakpointValues(["100%", null, "auto"], "width")}
        `,
    },
    cancelButton: {
        variant: "tertiary",
        css: css`
            ${x => x.theme.button.tertiary.css}
            ${breakpointValues(["100%", null, "auto"], "width")}
        `,
    },
    modal: {
        sizeVariant: "medium",
    },
    modalActions: {
        css: css`
            display: flex;
            justify-content: flex-end;
            gap: ${pxToRem(10)};
            padding-top: ${pxToRem(16)};
            ${breakpointValues(["column", null, "row"], "flex-direction")}

            &:first-child {
                padding-top: 0;
            }
        `,
    },
};

const styles = orderUploadSubmitButtonStyles;

const OrderUploadSubmitButton = ({
    orderUploadState,
    fields: { confirmationText },
    createCartUpload,
    submitUploadedOrder,
    clearUploadedFileStatus,
    signInUrl,
}: Props) => {
    const { canSubmitOrder, document, orderSubmitted, webOrderNumber, cartIsCreated } = orderUploadState;
    const toasterContext = useContext(ToasterContext);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    const modalOpenHandler = () => {
        setIsModalOpen(true);
    };

    const modalCloseHandler = () => {
        setIsModalOpen(false);
    };

    const requestConfirmationOrderUploadHandler = () => {
        modalOpenHandler();
    };

    useEffect(() => {
        if (cartIsCreated) {
            submitUploadedOrder({
                documents: [document],
                webOrderNumber,
            });
            clearUploadedFileStatus({});
            toasterContext.addToast({
                body: translate("Order {0} submitted.", `# ${webOrderNumber}`),
                messageType: "success",
            });
        }
    }, [cartIsCreated]);

    const submitOrderUploadHandler = () => {
        createCartUpload({
            onError: error => {
                if ((error as any).statusCode === 401) {
                    window.location.href = signInUrl!;
                } else {
                    toasterContext.addToast({
                        body: translate("An error occured. Try again later."),
                        messageType: "danger",
                        timeoutLength: 4000,
                    });
                }
            },
        });

        modalCloseHandler();
    };

    const cancelOrderUploadHandler = () => {
        modalCloseHandler();
    };

    return (
        <>
            <StyledWrapper {...styles.wrapper}>
                <Button
                    onClick={requestConfirmationOrderUploadHandler}
                    {...styles.confirmButton}
                    disabled={!canSubmitOrder || orderSubmitted}
                >
                    {translate("Submit Order")}
                </Button>
            </StyledWrapper>
            <Modal
                {...styles.modal}
                headline={translate("Please confirm before placing your order")}
                isOpen={isModalOpen}
                handleClose={modalCloseHandler}
            >
                <Typography>{confirmationText}</Typography>
                <StyledWrapper {...styles.modalActions}>
                    <Button onClick={cancelOrderUploadHandler} {...styles.cancelButton}>
                        {translate("Cancel")}
                    </Button>
                    <Button onClick={submitOrderUploadHandler} {...styles.submitButton}>
                        {translate("Submit Order")}
                    </Button>
                </StyledWrapper>
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderUploadSubmitButton),
    definition: {
        group: "Order Upload",
        displayName: "Greif Submit Button",
        allowedContexts: ["OrderUploadPage"],
        fieldDefinitions: [
            {
                name: fields.confirmationText,
                displayName: "Confirmation Text",
                editorTemplate: "MultilineTextField",
                defaultValue: "Are you sure you want to proceed with placing your order?",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
